import { COUNTRIES, type CountryCode } from '@keel/core';

const regionName = (code: string): string => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

/** Select options: "Australia (AUD)" … */
export const COUNTRY_OPTIONS = (Object.keys(COUNTRIES) as CountryCode[]).map((c) => ({
  value: c,
  label: c === 'OTHER' ? `Somewhere else (${COUNTRIES[c].currency})` : `${regionName(c)} (${COUNTRIES[c].currency})`,
}));

/** Country for a BCP-47 locale's region ("en-AU" → AU), if supported. */
export const countryFromLocale = (locale: string | undefined): CountryCode | undefined => {
  const region = locale?.split('-')[1]?.toUpperCase();
  return region && region in COUNTRIES ? (region as CountryCode) : undefined;
};

/** Country from the browser's time zone (Australia/Sydney → AU, Europe/London → GB, America/… → US). */
export const countryFromTimeZone = (tz: string | undefined): CountryCode | undefined => {
  if (!tz) return undefined;
  if (tz.startsWith('Australia/')) return 'AU';
  if (tz === 'Europe/London' || tz === 'Europe/Belfast') return 'GB';
  if (tz === 'Pacific/Auckland') return 'NZ';
  if (/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Indiana|Kentucky|Boise)|^Pacific\/Honolulu/.test(tz)) return 'US';
  return undefined;
};

/** A `?country=` value from the marketing site or a deep link ("UK" is the site's code for GB). */
export const countryFromParam = (v: string | null | undefined): CountryCode | undefined => {
  const c = v?.toUpperCase() === 'UK' ? 'GB' : v?.toUpperCase();
  return c && c in COUNTRIES ? (c as CountryCode) : undefined;
};

/**
 * Best guess at the user's country: their saved locale, then the browser's time zone (where they are), then its
 * language (often en-GB or en-US whatever the country), then Australia.
 */
export const guessCountry = (userLocale?: string): CountryCode => {
  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } })();
  return countryFromLocale(userLocale) ?? countryFromTimeZone(tz) ?? countryFromLocale(typeof navigator !== 'undefined' ? navigator.language : undefined) ?? 'AU';
};

/** Distinct currencies across supported countries, for display-currency pickers. */
export const CURRENCY_OPTIONS = [...new Set(Object.values(COUNTRIES).map((c) => c.currency))].map((c) => ({ value: c, label: c }));
export const LOCALE_OPTIONS = [
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-NZ', label: 'English (New Zealand)' },
];
